import { forwardRef, type MouseEvent } from 'react';

import { AlertStatusBadge } from '@/alerts/view/components/alert-status-badge';
import { cls } from '@/utils/cls';
import { formatDate } from '@/utils/format/date';

import type { AlertSuggestion } from '../entities/alert-suggestion';
import type { DropData } from '../types/drop-data';

import { AlertTypeBadge } from './alert-type-badge';

export type AlertSuggestionCardProps = {
  draggable?: boolean;
  alertSuggestion: AlertSuggestion;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  className?: string;
};

const AlertSuggestionCard = forwardRef<
  HTMLDivElement,
  AlertSuggestionCardProps
>(({ alertSuggestion, draggable, onClick, className }, ref) => (
  <div
    ref={ref}
    draggable={draggable}
    onClick={onClick}
    onDragStart={function (e) {
      const drop: DropData = {
        type: 'create-task-from-alert',
        alertSuggestion: alertSuggestion,
      };
      e.dataTransfer.setData('text/plain', JSON.stringify(drop));
    }}
    className={cls(
      'space-y-2 bg-white/60 p-4 hover:bg-gray-50',
      {
        'cursor-pointer': draggable,
      },
      className,
    )}
    data-testid="task-suggested-card"
  >
    <div className="flex flex-row items-baseline gap-4">
      <div
        className="grow truncate text-base font-normal leading-normal text-neutral-700"
        data-testid="task-suggested-card-id"
      >
        {alertSuggestion.frontendId}
      </div>
      <div className="shrink-0 text-base font-light leading-normal text-neutral-500">
        <AlertStatusBadge status={alertSuggestion.status} />
      </div>
    </div>
    <div className="flex items-center justify-start">
      {alertSuggestion.type ? (
        <AlertTypeBadge type={alertSuggestion.type} className="-ml-1" />
      ) : null}
    </div>
    <div className="flex flex-row items-end gap-4">
      <div className="line-clamp-2 h-full grow text-base font-normal leading-normal text-neutral-500">
        {alertSuggestion.suggestedAction}
      </div>
      <div className="shrink-0">
        <div className="font-light text-neutral-500">
          {formatDate(alertSuggestion.changedAt)}
        </div>
      </div>
    </div>
  </div>
));

AlertSuggestionCard.displayName = 'AlertSuggestionCard';
export { AlertSuggestionCard };
